import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { BackgroundBlobs } from '../../components/ui/BackgroundBlobs';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
    Search, Download, RefreshCw, CheckCircle, AlertTriangle, 
    Edit, Package, Clock, Eye, EyeOff, Smartphone, Sparkles, Zap,
    ChevronDown, X, Battery, CheckSquare, Save, FolderOpen, Trash2, Plus,
    Tag, Settings, Euro
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Multi-select dropdown component
const MultiSelectFilter = ({ label, options, selected, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (option) => {
        if (selected.includes(option)) {
            onChange(selected.filter(s => s !== option));
        } else {
            onChange([...selected, option]);
        }
    };

    const clearAll = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 hover:border-slate-300 transition-colors min-w-[140px] text-sm"
            >
                <span className="truncate text-slate-700">
                    {selected.length === 0 ? placeholder : `${selected.length} sélectionné${selected.length > 1 ? 's' : ''}`}
                </span>
                <div className="flex items-center gap-1">
                    {selected.length > 0 && (
                        <span 
                            onClick={clearAll}
                            className="w-4 h-4 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center"
                        >
                            <X className="w-3 h-3 text-slate-500" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>
            
            {isOpen && (
                <div className="absolute z-50 mt-1 w-56 bg-white rounded-xl border border-slate-200 shadow-lg py-2 max-h-64 overflow-y-auto">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-400">Aucune option</div>
                    ) : (
                        options.map((option) => (
                            <label
                                key={option}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                            >
                                <Checkbox
                                    checked={selected.includes(option)}
                                    onCheckedChange={() => toggleOption(option)}
                                />
                                <span className="text-sm text-slate-700 truncate">{option}</span>
                            </label>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

const AdminProducts = () => {
    const { token } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingErp, setSyncingErp] = useState(false);
    const [syncingArgus, setSyncingArgus] = useState(false);
    const [applyingPrices, setApplyingPrices] = useState(false);
    const [loadingMargins, setLoadingMargins] = useState(false);
    const [margins, setMargins] = useState({});
    const [total, setTotal] = useState(0);
    const [syncStatus, setSyncStatus] = useState({ erp: null, argus: null });
    
    const [search, setSearch] = useState('');
    const [brandFilter, setBrandFilter] = useState([]);
    const [modelFilter, setModelFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState([]);
    const [erpStatusFilter, setErpStatusFilter] = useState([]);
    const [onlineFilter, setOnlineFilter] = useState([]);
    const [availabilityFilter, setAvailabilityFilter] = useState([]);
    const [publishedFilter, setPublishedFilter] = useState([]);
    const [defectGroupFilter, setDefectGroupFilter] = useState([]);
    const [lockStatusFilter, setLockStatusFilter] = useState([]);
    
    // Saved views
    const [savedViews, setSavedViews] = useState([]);
    const [selectedView, setSelectedView] = useState(null);
    const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const [savingView, setSavingView] = useState(false);
    
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [bulkPublishing, setBulkPublishing] = useState(false);
    
    const [editingProduct, setEditingProduct] = useState(null);
    const [manualPrice, setManualPrice] = useState('');
    const [savingPrice, setSavingPrice] = useState(false);
    const [marginSimulation, setMarginSimulation] = useState(null);
    const [loadingSimulation, setLoadingSimulation] = useState(false);
    
    // Badge management
    const [badges, setBadges] = useState([]);
    const [badgeModalOpen, setBadgeModalOpen] = useState(false);
    const [badgeManagerOpen, setBadgeManagerOpen] = useState(false);
    const [selectedProductForBadge, setSelectedProductForBadge] = useState(null);
    const [newBadgeName, setNewBadgeName] = useState('');
    const [newBadgeColor, setNewBadgeColor] = useState('#f97316');
    const [editingBadge, setEditingBadge] = useState(null);
    
    // Bulk price management
    const [bulkPriceModalOpen, setBulkPriceModalOpen] = useState(false);
    const [bulkPrice, setBulkPrice] = useState('');
    const [applyingBulkPrice, setApplyingBulkPrice] = useState(false);

    // Get current filters object
    const getCurrentFilters = () => ({
        search,
        brandFilter,
        modelFilter,
        statusFilter,
        erpStatusFilter,
        onlineFilter,
        availabilityFilter,
        publishedFilter,
        defectGroupFilter,
        lockStatusFilter
    });

    // Apply filters from a saved view
    const applyFilters = (filters) => {
        if (filters.search !== undefined) setSearch(filters.search);
        if (filters.brandFilter) setBrandFilter(filters.brandFilter);
        if (filters.modelFilter) setModelFilter(filters.modelFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.erpStatusFilter) setErpStatusFilter(filters.erpStatusFilter);
        if (filters.onlineFilter) setOnlineFilter(filters.onlineFilter);
        if (filters.availabilityFilter) setAvailabilityFilter(filters.availabilityFilter);
        if (filters.publishedFilter) setPublishedFilter(filters.publishedFilter);
        if (filters.defectGroupFilter) setDefectGroupFilter(filters.defectGroupFilter);
        if (filters.lockStatusFilter) setLockStatusFilter(filters.lockStatusFilter);
    };

    // Reset all filters
    const resetFilters = () => {
        setSearch('');
        setBrandFilter([]);
        setModelFilter([]);
        setStatusFilter([]);
        setErpStatusFilter([]);
        setOnlineFilter([]);
        setAvailabilityFilter([]);
        setPublishedFilter([]);
        setDefectGroupFilter([]);
        setLockStatusFilter([]);
        setSelectedView(null);
    };

    // Fetch saved views
    const fetchSavedViews = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/saved-views`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedViews(response.data.views || []);
        } catch (error) {
            console.error('Error fetching saved views:', error);
        }
    }, [token]);

    // Save current view
    const handleSaveView = async () => {
        if (!newViewName.trim()) {
            toast.error('Veuillez entrer un nom pour la vue');
            return;
        }
        
        setSavingView(true);
        try {
            const response = await axios.post(
                `${API_URL}/admin/saved-views`,
                { name: newViewName.trim(), filters: getCurrentFilters() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Vue "${newViewName}" créée`);
            setSaveViewDialogOpen(false);
            setNewViewName('');
            fetchSavedViews();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSavingView(false);
        }
    };

    // Update existing view
    const handleUpdateView = async (viewId) => {
        try {
            await axios.put(
                `${API_URL}/admin/saved-views/${viewId}`,
                { filters: getCurrentFilters() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Vue mise à jour');
            fetchSavedViews();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Delete a view
    const handleDeleteView = async (viewId, viewName) => {
        if (!confirm(`Supprimer la vue "${viewName}" ?`)) return;
        
        try {
            await axios.delete(
                `${API_URL}/admin/saved-views/${viewId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Vue supprimée');
            if (selectedView === viewId) setSelectedView(null);
            fetchSavedViews();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Load a saved view
    const loadView = (view) => {
        setSelectedView(view.id);
        applyFilters(view.filters);
        toast.success(`Vue "${view.name}" chargée`);
    };

    // Fetch badges
    const fetchBadges = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/badges`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBadges(response.data.badges || []);
        } catch (error) {
            console.error('Error fetching badges:', error);
        }
    }, [token]);

    // Create badge
    const handleCreateBadge = async () => {
        if (!newBadgeName.trim()) {
            toast.error('Veuillez entrer un nom pour le badge');
            return;
        }
        try {
            await axios.post(`${API_URL}/admin/badges`, {
                name: newBadgeName,
                bg_color: newBadgeColor,
                text_color: '#ffffff',
                icon: 'tag'
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Badge créé');
            setNewBadgeName('');
            setNewBadgeColor('#f97316');
            fetchBadges();
        } catch (error) {
            toast.error('Erreur lors de la création du badge');
        }
    };

    // Update badge
    const handleUpdateBadge = async (badgeId, updates) => {
        try {
            await axios.put(`${API_URL}/admin/badges/${badgeId}`, updates, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Badge mis à jour');
            fetchBadges();
            fetchProducts(); // Refresh products to show updated badge
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    // Delete badge
    const handleDeleteBadge = async (badgeId) => {
        if (!window.confirm('Supprimer ce badge ? Il sera retiré de tous les produits.')) return;
        try {
            await axios.delete(`${API_URL}/admin/badges/${badgeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Badge supprimé');
            fetchBadges();
            fetchProducts();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Apply badge to product
    const handleApplyBadge = async (productId, badgeId) => {
        try {
            if (badgeId) {
                await axios.post(`${API_URL}/admin/products/${productId}/badge`, 
                    { badge_id: badgeId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Badge appliqué');
            } else {
                await axios.delete(`${API_URL}/admin/products/${productId}/badge`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Badge retiré');
            }
            fetchProducts();
            setBadgeModalOpen(false);
            setSelectedProductForBadge(null);
        } catch (error) {
            toast.error('Erreur lors de l\'application du badge');
        }
    };

    // Apply badge to selected products (bulk)
    const handleBulkApplyBadge = async (badgeId) => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez des produits');
            return;
        }
        try {
            await axios.post(`${API_URL}/admin/products/bulk-badge`, {
                product_ids: selectedProducts,
                badge_id: badgeId || null
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(badgeId ? 'Badge appliqué aux produits sélectionnés' : 'Badge retiré des produits sélectionnés');
            fetchProducts();
            setSelectedProducts([]);
        } catch (error) {
            toast.error('Erreur lors de l\'application en masse');
        }
    };

    // Apply bulk manual price
    const handleBulkManualPrice = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez des produits');
            return;
        }
        
        const priceValue = parseFloat(bulkPrice);
        if (!priceValue || priceValue <= 0) {
            toast.error('Veuillez entrer un prix valide');
            return;
        }
        
        setApplyingBulkPrice(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/bulk-manual-price`, {
                product_ids: selectedProducts,
                price: priceValue
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            toast.success(response.data.message);
            setBulkPriceModalOpen(false);
            setBulkPrice('');
            fetchProducts();
            setSelectedProducts([]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'application du prix');
        } finally {
            setApplyingBulkPrice(false);
        }
    };

    // Clear bulk manual prices
    const handleBulkClearManualPrice = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez des produits');
            return;
        }
        
        if (!window.confirm(`Retirer le prix manuel de ${selectedProducts.length} produit(s) et recalculer ?`)) {
            return;
        }
        
        setApplyingBulkPrice(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/bulk-manual-price`, {
                product_ids: selectedProducts,
                price: null
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            toast.success(response.data.message);
            fetchProducts();
            setSelectedProducts([]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression du prix');
        } finally {
            setApplyingBulkPrice(false);
        }
    };

    const fetchProducts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/products`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProducts(response.data.products || []);
            setTotal(response.data.total || 0);
        } catch (error) {
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchProducts();
        fetchSavedViews();
        fetchBadges();
    }, [fetchProducts, fetchSavedViews, fetchBadges]);

    const fetchMargins = useCallback(async (productsList) => {
        if (!productsList || productsList.length === 0) return;
        setLoadingMargins(true);
        try {
            const imeis = productsList.filter(p => p.imei && p.price).slice(0, 100).map(p => p.imei);
            if (imeis.length === 0) return;
            const response = await axios.post(`${API_URL}/admin/products/margins-batch`, { imeis }, 
                { headers: { Authorization: `Bearer ${token}` } });
            if (response.data.margins) setMargins(response.data.margins);
        } catch (error) {
            console.error('Fetch margins error:', error);
        } finally {
            setLoadingMargins(false);
        }
    }, [token]);

    useEffect(() => {
        if (products.length > 0) fetchMargins(products);
    }, [products, fetchMargins]);

    const fetchSyncStatus = useCallback(async () => {
        try {
            const [erpRes, argusRes] = await Promise.all([
                axios.get(`${API_URL}/sync/status`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/admin/argus/status`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setSyncStatus({ erp: erpRes.data, argus: argusRes.data });
        } catch (error) {
            console.error('Fetch sync status error:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchSyncStatus();
        // Refresh sync status every 30 seconds
        const interval = setInterval(fetchSyncStatus, 30000);
        return () => clearInterval(interval);
    }, [fetchSyncStatus]);

    const handleSyncErp = async () => {
        setSyncingErp(true);
        try {
            const response = await axios.post(`${API_URL}/admin/sync-erp`, {}, { headers: { Authorization: `Bearer ${token}` } });
            const removed = response.data.removed || 0;
            const pricesApplied = response.data.prices_applied || 0;
            let msg = `ERP: ${response.data.inserted} ajoutés, ${response.data.updated} mis à jour`;
            if (removed > 0) msg += `, ${removed} retirés`;
            if (pricesApplied > 0) msg += `, ${pricesApplied} prix appliqués`;
            toast.success(msg);
            fetchProducts();
            fetchSyncStatus();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur sync ERP');
        } finally {
            setSyncingErp(false);
        }
    };

    const handleSyncArgus = async () => {
        setSyncingArgus(true);
        try {
            const response = await axios.post(`${API_URL}/admin/argus/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message);
            fetchSyncStatus();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur sync Argus');
        } finally {
            setSyncingArgus(false);
        }
    };

    const handleApplyPrices = async () => {
        setApplyingPrices(true);
        try {
            const response = await axios.post(`${API_URL}/admin/pricing/recalculate-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${response.data.updated} produits mis à jour`);
            fetchProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur calcul prix');
        } finally {
            setApplyingPrices(false);
        }
    };

    const handleOpenPriceEditor = (product) => {
        setEditingProduct(product);
        setManualPrice(product.price?.toString() || '');
        setMarginSimulation(null);
    };

    const fetchMarginSimulation = useCallback(async (imei, price) => {
        if (!imei || !price || isNaN(parseFloat(price))) {
            setMarginSimulation(null);
            return;
        }
        setLoadingSimulation(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/margin-simulation`,
                { imei, prix_vente: parseFloat(price) },
                { headers: { Authorization: `Bearer ${token}` } });
            setMarginSimulation(response.data);
        } catch (error) {
            setMarginSimulation(null);
        } finally {
            setLoadingSimulation(false);
        }
    }, [token]);

    useEffect(() => {
        if (!editingProduct || !manualPrice) {
            setMarginSimulation(null);
            return;
        }
        const timer = setTimeout(() => fetchMarginSimulation(editingProduct.imei, manualPrice), 500);
        return () => clearTimeout(timer);
    }, [editingProduct, manualPrice, fetchMarginSimulation]);

    const handleSavePrice = async () => {
        if (!editingProduct) return;
        setSavingPrice(true);
        try {
            const priceValue = manualPrice ? parseFloat(manualPrice) : null;
            await axios.put(`${API_URL}/admin/products/${editingProduct.id}/manual-price`,
                { price: priceValue },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success(priceValue ? 'Prix manuel enregistré' : 'Prix automatique restauré');
            setEditingProduct(null);
            fetchProducts();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSavingPrice(false);
        }
    };

    // Toggle publish for a single product
    const handleTogglePublish = async (productId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await axios.put(`${API_URL}/admin/products/${productId}/publish?publish=${newStatus}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(newStatus ? 'Produit publié' : 'Produit dépublié');
            // Update local state
            setProducts(prev => prev.map(p => 
                p.id === productId ? { ...p, is_published: newStatus } : p
            ));
        } catch (error) {
            toast.error('Erreur lors de la publication');
        }
    };

    // Bulk publish/unpublish
    const handleBulkPublish = async (publish) => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        setBulkPublishing(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/bulk-publish`, {
                product_ids: selectedProducts,
                publish: publish
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
            setSelectedProducts([]);
            fetchProducts();
        } catch (error) {
            toast.error('Erreur lors de la publication en masse');
        } finally {
            setBulkPublishing(false);
        }
    };

    // Mark single product as new (starts 72h countdown for Nouveautés)
    const [markingAsNew, setMarkingAsNew] = useState({});
    
    const handleMarkAsNew = async (productId) => {
        setMarkingAsNew(prev => ({ ...prev, [productId]: true }));
        try {
            await axios.put(`${API_URL}/admin/products/${productId}/mark-as-new`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Produit marqué comme nouveau (72h dans Nouveautés)');
            // Update local state with published_at
            setProducts(prev => prev.map(p => 
                p.id === productId ? { ...p, published_at: new Date().toISOString() } : p
            ));
        } catch (error) {
            toast.error('Erreur lors du marquage comme nouveau');
        } finally {
            setMarkingAsNew(prev => ({ ...prev, [productId]: false }));
        }
    };

    // Remove single product from Nouveautés
    const handleRemoveFromNew = async (productId) => {
        setMarkingAsNew(prev => ({ ...prev, [productId]: true }));
        try {
            await axios.put(`${API_URL}/admin/products/${productId}/remove-from-new`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Produit retiré des Nouveautés');
            // Update local state - remove published_at
            setProducts(prev => prev.map(p => 
                p.id === productId ? { ...p, published_at: null } : p
            ));
        } catch (error) {
            toast.error('Erreur lors du retrait des Nouveautés');
        } finally {
            setMarkingAsNew(prev => ({ ...prev, [productId]: false }));
        }
    };

    // Bulk mark as new
    const [bulkMarkingNew, setBulkMarkingNew] = useState(false);
    
    const handleBulkMarkAsNew = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        setBulkMarkingNew(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/bulk-mark-as-new`, 
                selectedProducts,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message);
            setSelectedProducts([]);
            fetchProducts();
        } catch (error) {
            toast.error('Erreur lors du marquage en masse');
        } finally {
            setBulkMarkingNew(false);
        }
    };

    // Bulk remove from new
    const handleBulkRemoveFromNew = async () => {
        if (selectedProducts.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        setBulkMarkingNew(true);
        try {
            const response = await axios.post(`${API_URL}/admin/products/bulk-remove-from-new`, 
                selectedProducts,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(response.data.message);
            setSelectedProducts([]);
            fetchProducts();
        } catch (error) {
            toast.error('Erreur lors du retrait en masse');
        } finally {
            setBulkMarkingNew(false);
        }
    };

    // Toggle select product
    const toggleSelectProduct = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) 
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    // Select all filtered products
    const selectAllFiltered = () => {
        const allIds = filteredProducts.map(p => p.id);
        if (selectedProducts.length === allIds.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(allIds);
        }
    };

    const exportToCSV = () => {
        const headers = ['Marque', 'Modèle', 'Capacité', 'Grade', 'État', 'Prix Vente', 'IMEI'];
        const rows = filteredProducts.map(p => [
            p.brand, p.model, p.storage_capacity, p.grade || '', 
            p.aesthetic_condition || '', p.price || '', p.imei
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stock_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const brands = [...new Set(products.map(p => p.brand))].sort();
    const models = [...new Set(products.filter(p => brandFilter.length === 0 || brandFilter.includes(p.brand)).map(p => p.model))].sort();
    const erpStatuses = [...new Set(products.map(p => p.erp_statut).filter(Boolean))].sort();
    const conditions = [...new Set(products.map(p => p.aesthetic_condition).filter(Boolean))].sort();

    const filteredProducts = products.filter(p => {
        if (search) {
            const s = search.toLowerCase();
            if (!p.brand?.toLowerCase().includes(s) && !p.model?.toLowerCase().includes(s) && !p.imei?.includes(search)) return false;
        }
        if (brandFilter.length > 0 && !brandFilter.includes(p.brand)) return false;
        if (modelFilter.length > 0 && !modelFilter.includes(p.model)) return false;
        if (statusFilter.length > 0 && !statusFilter.includes(p.aesthetic_condition)) return false;
        if (erpStatusFilter.length > 0 && !erpStatusFilter.includes(p.erp_statut)) return false;
        if (onlineFilter.length > 0) {
            // "En ligne" = valid ERP status + valid price
            const VALID_ERP_STATUSES = ['Disponible Export', 'Prêt à vendre B2B'];
            const SOLD_ERP_STATUSES = ['Vendu', 'Expédié B2B', 'Attente expédition B2B', 'Retiré', 'SAV', 'Défectueux'];
            const hasValidErpStatus = VALID_ERP_STATUSES.includes(p.erp_statut);
            const hasValidPrice = p.has_valid_price || p.manual_price;
            const isOnline = hasValidErpStatus && hasValidPrice && p.is_available;
            const isSold = SOLD_ERP_STATUSES.includes(p.erp_statut);
            
            // Pour "Hors ligne", on veut les produits qui ont un problème à corriger (pas les vendus)
            let productStatus;
            if (isSold) {
                productStatus = 'Vendu';
            } else if (isOnline) {
                productStatus = 'En ligne';
            } else {
                productStatus = 'Hors ligne';
            }
            if (!onlineFilter.includes(productStatus)) return false;
        }
        if (availabilityFilter.length > 0) {
            // Check ERP status for "En vente"
            const VALID_ERP_STATUSES = ['Disponible Export', 'Prêt à vendre B2B'];
            const hasValidErpStatus = VALID_ERP_STATUSES.includes(p.erp_statut);
            
            let availability = 'Indisponible';
            if (p.sold_to_order) {
                availability = 'Vendu';
            } else if (p.reserved_by) {
                availability = 'Réservé';
            } else if (p.is_available && hasValidErpStatus) {
                availability = 'En vente';
            }
            if (!availabilityFilter.includes(availability)) return false;
        }
        // Published filter
        if (publishedFilter.length > 0) {
            const isPublished = p.is_published === true;
            const publishStatus = isPublished ? 'Publié' : 'Non publié';
            if (!publishedFilter.includes(publishStatus)) return false;
        }
        // Defect Group filter (Panne majeure / groupe comme niveau 3, CRITIQUE)
        if (defectGroupFilter.length > 0) {
            const deductions = p.pricing_deductions || [];
            // Seul le type "major_defect" représente les pannes de groupe (niveau 3, CRITIQUE, LIGHT, decote)
            // Les pannes basiques individuelles (Back Camera, Face Id) ont le type "basic_defect"
            const hasGroupDefect = deductions.some(d => d.type === 'major_defect');
            
            if (defectGroupFilter.includes('Avec panne groupe') && !hasGroupDefect) return false;
            if (defectGroupFilter.includes('Sans panne groupe') && hasGroupDefect) return false;
        }
        // Lock status filter
        if (lockStatusFilter.length > 0) {
            const lockStatus = p.lock_status || 'Non défini';
            if (!lockStatusFilter.includes(lockStatus)) return false;
        }
        return true;
    });

    // Stats also use the same logic
    const VALID_ERP_STATUSES = ['Disponible Export', 'Prêt à vendre B2B'];
    // Statuts ERP qui indiquent un produit vendu/non disponible
    const SOLD_ERP_STATUSES = ['Vendu', 'Expédié B2B', 'Attente expédition B2B', 'Retiré', 'SAV', 'Défectueux'];
    
    const stats = {
        total: products.length,
        published: products.filter(p => p.is_published === true).length,
        unpublished: products.filter(p => p.is_published !== true).length,
        enVente: products.filter(p => 
            p.is_available && 
            p.is_published === true &&
            (p.has_valid_price || p.manual_price) && 
            VALID_ERP_STATUSES.includes(p.erp_statut)
        ).length,
        // Hors ligne = produits qui POURRAIENT être vendus mais ont un problème
        // Exclure les produits déjà vendus ou retirés
        horsLigne: products.filter(p => {
            // Exclure les produits vendus/retirés
            if (SOLD_ERP_STATUSES.includes(p.erp_statut)) return false;
            // Inclure seulement les produits avec un problème de prix ou de statut
            const hasPriceIssue = !p.has_valid_price && !p.manual_price;
            const hasStatusIssue = !VALID_ERP_STATUSES.includes(p.erp_statut) && !SOLD_ERP_STATUSES.includes(p.erp_statut);
            return hasPriceIssue || hasStatusIssue;
        }).length,
        vendu: products.filter(p => SOLD_ERP_STATUSES.includes(p.erp_statut)).length,
        sansArgus: products.filter(p => 
            !SOLD_ERP_STATUSES.includes(p.erp_statut) && 
            (!p.argus_price || p.argus_price === 0)
        ).length,
        reserved: products.filter(p => p.reserved_by).length
    };

    // Fetch missing margins for currently filtered/visible products
    useEffect(() => {
        const fetchMissingMargins = async () => {
            // Get visible products that don't have margins yet
            const visibleProducts = filteredProducts.slice(0, 100);
            const missingMargins = visibleProducts.filter(p => p.imei && p.price && !margins[p.imei]);
            
            if (missingMargins.length > 0) {
                setLoadingMargins(true);
                try {
                    const imeis = missingMargins.map(p => p.imei);
                    const response = await axios.post(`${API_URL}/admin/products/margins-batch`, { imeis }, 
                        { headers: { Authorization: `Bearer ${token}` } });
                    if (response.data.margins) {
                        setMargins(prev => ({ ...prev, ...response.data.margins }));
                    }
                } catch (error) {
                    console.error('Fetch missing margins error:', error);
                } finally {
                    setLoadingMargins(false);
                }
            }
        };
        
        // Small delay to avoid too many requests when filters change rapidly
        const timer = setTimeout(fetchMissingMargins, 500);
        return () => clearTimeout(timer);
    }, [filteredProducts, token]);

    const getGradeBadge = (grade) => {
        const styles = {
            'A': 'bg-emerald-100 text-emerald-800 border-emerald-300',
            'A+': 'bg-emerald-100 text-emerald-800 border-emerald-300',
            'B': 'bg-blue-100 text-blue-800 border-blue-300',
            'C': 'bg-amber-100 text-amber-800 border-amber-300',
            'D': 'bg-rose-100 text-rose-800 border-rose-300'
        };
        return styles[grade] || 'bg-slate-100 text-slate-600 border-slate-200';
    };

    const getBatteryDisplay = (battery) => {
        if (!battery) return null;
        const batteryMap = {
            'neuve': { label: '100%', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
            'sup_90': { label: '>90%', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
            'sup_85': { label: '>85%', color: 'bg-green-100 text-green-700 border-green-300' },
            'sup_80': { label: '>80%', color: 'bg-lime-100 text-lime-700 border-lime-300' },
            'inf_80': { label: '<80%', color: 'bg-orange-100 text-orange-700 border-orange-300' },
            'ok': { label: 'OK', color: 'bg-blue-100 text-blue-700 border-blue-300' },
            'hs': { label: 'HS', color: 'bg-red-100 text-red-700 border-red-300' },
        };
        return batteryMap[battery] || { label: battery, color: 'bg-slate-100 text-slate-600 border-slate-200' };
    };

    const getConditionBadge = (condition) => {
        if (condition === 'Fonctionnel') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
        if (condition === 'À réparer') return 'bg-amber-100 text-amber-800 border-amber-300';
        return 'bg-slate-100 text-slate-600 border-slate-200';
    };

    return (
        <AdminLayout>
            <div className="min-h-screen bg-white relative">
                <BackgroundBlobs />
                
                {/* Header */}
                <div className="glass sticky top-0 z-20 border-b border-white/20">
                    <div className="px-6 py-5">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <h1 
                                    className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3"
                                    style={{ fontFamily: 'var(--font-heading)' }}
                                >
                                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center">
                                        <Package className="w-5 h-5 text-white" />
                                    </div>
                                    Stock
                                </h1>
                                <p className="text-sm text-slate-500 mt-1 ml-13">
                                    Gestion des appareils en inventaire
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={exportToCSV} 
                                    className="rounded-full border-slate-200 hover:bg-slate-50 font-medium"
                                >
                                    <Download className="w-4 h-4 mr-1.5" />
                                    Export
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={handleSyncErp} 
                                    disabled={syncingErp} 
                                    className="rounded-full border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-1.5 ${syncingErp ? 'animate-spin' : ''}`} />
                                    Sync ERP
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={handleSyncArgus} 
                                    disabled={syncingArgus} 
                                    className="rounded-full border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-1.5 ${syncingArgus ? 'animate-spin' : ''}`} />
                                    Sync Argus
                                </Button>
                                <Button 
                                    size="sm" 
                                    onClick={handleApplyPrices} 
                                    disabled={applyingPrices} 
                                    className="rounded-full bg-lime-500 text-slate-900 font-bold hover:scale-105 transition-transform btn-glow-green"
                                >
                                    <Zap className={`w-4 h-4 mr-1.5 ${applyingPrices ? 'animate-pulse' : ''}`} />
                                    Appliquer les prix
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setBadgeManagerOpen(true)} 
                                    className="rounded-full border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 font-medium"
                                >
                                    <Tag className="w-4 h-4 mr-1.5" />
                                    Badges ({badges.length})
                                </Button>
                            </div>
                        </div>

                        {/* Sync Status */}
                        <div className="flex items-center gap-6 text-xs text-slate-500 mb-5">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                                <span>ERP auto 5min:</span>
                                <span className="font-semibold text-slate-700">
                                    {syncStatus.erp?.last_sync ? new Date(syncStatus.erp.last_sync).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'Jamais'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-purple-500 rounded-full shadow-[0_0_8px_rgba(124,58,237,0.5)]"></span>
                                <span>Argus manuel:</span>
                                <span className="font-semibold text-slate-700">
                                    {syncStatus.argus?.last_sync ? `${new Date(syncStatus.argus.last_sync).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} (${syncStatus.argus.total_prices} prix)` : 'Jamais'}
                                </span>
                            </div>
                        </div>

                        {/* Stats Cards - Compact inline */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <div className="bg-white rounded-lg border border-slate-100 px-3 py-2 shadow-sm flex items-center gap-2">
                                <div className="w-6 h-6 bg-slate-100 rounded-md flex items-center justify-center">
                                    <Package className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-xs text-slate-500">Total</span>
                                <span className="text-lg font-bold text-slate-900 font-mono">{stats.total}</span>
                            </div>
                            <div 
                                className="bg-emerald-50 rounded-lg border border-emerald-200 px-3 py-2 shadow-sm flex items-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setPublishedFilter(publishedFilter.includes('Publié') ? [] : ['Publié'])}
                            >
                                <div className="w-6 h-6 bg-emerald-100 rounded-md flex items-center justify-center">
                                    <Eye className="w-3 h-3 text-emerald-600" />
                                </div>
                                <span className="text-xs text-emerald-600">Publié</span>
                                <span className="text-lg font-bold text-emerald-700 font-mono">{stats.published}</span>
                            </div>
                            <div 
                                className="bg-rose-50 rounded-lg border border-rose-200 px-3 py-2 shadow-sm flex items-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setPublishedFilter(publishedFilter.includes('Non publié') ? [] : ['Non publié'])}
                            >
                                <div className="w-6 h-6 bg-rose-100 rounded-md flex items-center justify-center">
                                    <EyeOff className="w-3 h-3 text-rose-600" />
                                </div>
                                <span className="text-xs text-rose-600">Non publié</span>
                                <span className="text-lg font-bold text-rose-700 font-mono">{stats.unpublished}</span>
                            </div>
                            <div className="bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 shadow-sm flex items-center gap-2">
                                <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                                    <Sparkles className="w-3 h-3 text-blue-600" />
                                </div>
                                <span className="text-xs text-blue-600">En ligne</span>
                                <span className="text-lg font-bold text-blue-700 font-mono">{stats.enVente}</span>
                            </div>
                            <div 
                                className="bg-amber-50 rounded-lg border border-amber-200 px-3 py-2 shadow-sm flex items-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => setOnlineFilter(onlineFilter.includes('Hors ligne') ? [] : ['Hors ligne'])}
                            >
                                <div className="w-6 h-6 bg-amber-100 rounded-md flex items-center justify-center">
                                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                                </div>
                                <span className="text-xs text-amber-600">Hors ligne</span>
                                <span className="text-lg font-bold text-amber-700 font-mono">{stats.horsLigne}</span>
                            </div>
                            <div className="bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 shadow-sm flex items-center gap-2">
                                <div className="w-6 h-6 bg-slate-100 rounded-md flex items-center justify-center">
                                    <CheckCircle className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-xs text-slate-500">Vendu</span>
                                <span className="text-lg font-bold text-slate-600 font-mono">{stats.vendu}</span>
                            </div>
                            <div className="bg-purple-50 rounded-lg border border-purple-200 px-3 py-2 shadow-sm flex items-center gap-2">
                                <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
                                    <Clock className="w-3 h-3 text-purple-600" />
                                </div>
                                <span className="text-xs text-purple-600">Réservés</span>
                                <span className="text-lg font-bold text-purple-700 font-mono">{stats.reserved}</span>
                            </div>
                        </div>

                        {/* Bulk Actions Bar */}
                        {selectedProducts.length > 0 && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Checkbox 
                                        checked={selectedProducts.length === filteredProducts.length}
                                        onCheckedChange={selectAllFiltered}
                                    />
                                    <span className="text-sm font-medium text-indigo-700">
                                        {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''} sélectionné{selectedProducts.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => handleBulkPublish(true)}
                                        disabled={bulkPublishing}
                                        className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600"
                                    >
                                        <Eye className="w-4 h-4 mr-1" />
                                        Publier
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleBulkPublish(false)}
                                        disabled={bulkPublishing}
                                        className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                                    >
                                        <EyeOff className="w-4 h-4 mr-1" />
                                        Dépublier
                                    </Button>
                                    
                                    {/* Bulk Mark as New */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleBulkMarkAsNew}
                                        disabled={bulkMarkingNew}
                                        className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50 bg-amber-50"
                                    >
                                        {bulkMarkingNew ? (
                                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                        ) : (
                                            <Sparkles className="w-4 h-4 mr-1" />
                                        )}
                                        Nouveau
                                    </Button>
                                    
                                    {/* Bulk Remove from New */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleBulkRemoveFromNew}
                                        disabled={bulkMarkingNew}
                                        className="rounded-full border-gray-300 text-gray-600 hover:bg-gray-100"
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Retirer Nouv.
                                    </Button>
                                    
                                    {/* Bulk Badge Dropdown */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="rounded-full border-orange-300 text-orange-600 hover:bg-orange-50"
                                            >
                                                <Tag className="w-4 h-4 mr-1" />
                                                Badge
                                                <ChevronDown className="w-3 h-3 ml-1" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start">
                                            {badges.map((badge) => (
                                                <DropdownMenuItem 
                                                    key={badge.id}
                                                    onClick={() => handleBulkApplyBadge(badge.id)}
                                                    className="flex items-center gap-2"
                                                >
                                                    <span 
                                                        className="px-2 py-0.5 text-xs font-bold rounded-full"
                                                        style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                                                    >
                                                        {badge.name}
                                                    </span>
                                                </DropdownMenuItem>
                                            ))}
                                            {badges.length > 0 && <DropdownMenuSeparator />}
                                            <DropdownMenuItem 
                                                onClick={() => handleBulkApplyBadge(null)}
                                                className="text-slate-500"
                                            >
                                                <X className="w-4 h-4 mr-2" />
                                                Retirer le badge
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    
                                    {/* Bulk Price Button */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setBulkPriceModalOpen(true)}
                                        className="rounded-full border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                                    >
                                        <Euro className="w-4 h-4 mr-1" />
                                        Prix
                                    </Button>
                                    
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setSelectedProducts([])}
                                        className="rounded-full"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Saved Views Bar */}
                        <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-3">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="rounded-full gap-2">
                                        <FolderOpen className="w-4 h-4" />
                                        {selectedView ? savedViews.find(v => v.id === selectedView)?.name || 'Vue' : 'Mes vues'}
                                        <ChevronDown className="w-3 h-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-64">
                                    {savedViews.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-slate-500">
                                            Aucune vue sauvegardée
                                        </div>
                                    ) : (
                                        <>
                                            {savedViews.map((view) => (
                                                <DropdownMenuItem key={view.id} className="flex items-center justify-between group cursor-pointer">
                                                    <span onClick={() => loadView(view)} className="flex-1">
                                                        {view.name}
                                                    </span>
                                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 p-0"
                                                            onClick={(e) => { e.stopPropagation(); handleUpdateView(view.id); }}
                                                            title="Mettre à jour avec les filtres actuels"
                                                        >
                                                            <RefreshCw className="w-3 h-3" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteView(view.id, view.name); }}
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </DropdownMenuItem>
                                            ))}
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem onClick={() => setSaveViewDialogOpen(true)} className="text-blue-600">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Sauvegarder la vue actuelle
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            
                            {selectedView && (
                                <Badge variant="secondary" className="gap-1">
                                    Vue: {savedViews.find(v => v.id === selectedView)?.name}
                                    <X className="w-3 h-3 cursor-pointer" onClick={resetFilters} />
                                </Badge>
                            )}
                            
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="rounded-full text-slate-500 hover:text-slate-700"
                                onClick={() => setSaveViewDialogOpen(true)}
                            >
                                <Save className="w-4 h-4 mr-1" />
                                Sauvegarder
                            </Button>
                            
                            {(brandFilter.length > 0 || modelFilter.length > 0 || search || statusFilter.length > 0) && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="rounded-full text-red-500 hover:text-red-700"
                                    onClick={resetFilters}
                                >
                                    <X className="w-4 h-4 mr-1" />
                                    Réinitialiser
                                </Button>
                            )}
                        </div>

                        {/* Filter Bar */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="relative flex-1 max-w-xs">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Rechercher..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10 h-10 bg-white rounded-full border-slate-200 focus:border-purple-400 focus:ring-purple-200"
                                />
                            </div>
                            <MultiSelectFilter
                                label="Marque"
                                options={brands}
                                selected={brandFilter}
                                onChange={setBrandFilter}
                                placeholder="Marques"
                            />
                            <MultiSelectFilter
                                label="Modèle"
                                options={models}
                                selected={modelFilter}
                                onChange={setModelFilter}
                                placeholder="Modèles"
                            />
                            <MultiSelectFilter
                                label="Statut ERP"
                                options={erpStatuses}
                                selected={erpStatusFilter}
                                onChange={setErpStatusFilter}
                                placeholder="Statut ERP"
                            />
                            <MultiSelectFilter
                                label="État"
                                options={conditions}
                                selected={statusFilter}
                                onChange={setStatusFilter}
                                placeholder="État"
                            />
                            <MultiSelectFilter
                                label="Visibilité"
                                options={['En ligne', 'Hors ligne', 'Vendu']}
                                selected={onlineFilter}
                                onChange={setOnlineFilter}
                                placeholder="Visibilité"
                            />
                            <MultiSelectFilter
                                label="Publication"
                                options={['Publié', 'Non publié']}
                                selected={publishedFilter}
                                onChange={setPublishedFilter}
                                placeholder="Publication"
                            />
                            <MultiSelectFilter
                                label="Disponibilité"
                                options={['En vente', 'Réservé', 'Vendu', 'Indisponible']}
                                selected={availabilityFilter}
                                onChange={setAvailabilityFilter}
                                placeholder="Disponibilité"
                            />
                            <MultiSelectFilter
                                label="Panne Groupe"
                                options={['Avec panne groupe', 'Sans panne groupe']}
                                selected={defectGroupFilter}
                                onChange={setDefectGroupFilter}
                                placeholder="Panne Groupe"
                            />
                            <MultiSelectFilter
                                label="Verrouillage"
                                options={['Débloqué', 'iCloud', 'MDM', 'Opérateur', 'Non défini']}
                                selected={lockStatusFilter}
                                onChange={setLockStatusFilter}
                                placeholder="Verrouillage"
                            />
                            <div className="ml-auto flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={selectAllFiltered}
                                    className="rounded-full text-sm"
                                    data-testid="select-all-btn"
                                >
                                    <CheckSquare className="w-4 h-4 mr-1" />
                                    {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 
                                        ? 'Tout désélectionner' 
                                        : `Tout sélectionner (${filteredProducts.length})`}
                                </Button>
                                <span className="text-sm text-slate-500 font-medium">
                                    {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product List */}
                <div className="p-6 relative z-10">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Package className="w-10 h-10 text-purple-400" />
                            </div>
                            <p className="text-slate-500 text-lg">Aucun produit trouvé</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {filteredProducts.slice(0, 100).map((product) => {
                                const marginData = margins[product.imei];
                                const isOffline = !product.has_valid_price && !product.manual_price;
                                
                                return (
                                    <div 
                                        key={product.id}
                                        className={`bg-white rounded-2xl border p-5 transition-all duration-300 hover:shadow-[0_8px_30px_-8px_rgba(124,58,237,0.15)] hover:-translate-y-1 ${isOffline ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}
                                    >
                                        <div className="grid grid-cols-12 gap-4 items-center">
                                            {/* Checkbox for selection + Product Info - Col 1-4 */}
                                            <div className="col-span-4 flex items-center gap-3">
                                                <Checkbox 
                                                    checked={selectedProducts.includes(product.id)}
                                                    onCheckedChange={() => toggleSelectProduct(product.id)}
                                                    className="border-slate-300"
                                                />
                                                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl flex items-center justify-center flex-shrink-0 border border-purple-200">
                                                    <Smartphone className="w-7 h-7 text-purple-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 
                                                            className="font-bold text-slate-900 truncate"
                                                            style={{ fontFamily: 'var(--font-heading)' }}
                                                        >
                                                            {product.brand} {product.model}
                                                        </h3>
                                                        {product.is_published ? (
                                                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-full text-[10px] px-2 font-semibold">
                                                                Publié
                                                            </Badge>
                                                        ) : (
                                                            <Badge className="bg-rose-100 text-rose-700 border border-rose-300 rounded-full text-[10px] px-2 font-semibold">
                                                                Non publié
                                                            </Badge>
                                                        )}
                                                        {isOffline && (
                                                            <Badge className="bg-amber-100 text-amber-700 border border-amber-300 rounded-full text-[10px] px-2 font-semibold">
                                                                Hors ligne
                                                            </Badge>
                                                        )}
                                                        {/* Custom Product Badge */}
                                                        {product.badge && (
                                                            <span 
                                                                className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                                                                style={{ 
                                                                    backgroundColor: product.badge.bg_color || '#f97316', 
                                                                    color: product.badge.text_color || '#ffffff' 
                                                                }}
                                                            >
                                                                {product.badge.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                                        <span className="font-mono font-semibold">
                                                            {product.storage_capacity ? `${product.storage_capacity}GB` : '-'}
                                                            {product.version && <span className="text-blue-600 ml-1">{product.version}</span>}
                                                        </span>
                                                        <span>•</span>
                                                        <span className="font-mono text-slate-400">{product.imei}</span>
                                                        {product.offline_reason && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="text-red-500 font-medium">{product.offline_reason}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Badges - Col 5-6 */}
                                            <div className="col-span-2 flex items-center gap-1.5 flex-wrap">
                                                {product.erp_statut && (
                                                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[10px] px-2 py-0.5">
                                                        {product.erp_statut}
                                                    </Badge>
                                                )}
                                                {product.grade && (
                                                    <Badge className={`border rounded-full text-[10px] px-2 py-0.5 font-bold ${getGradeBadge(product.grade)}`}>
                                                        {product.grade}
                                                    </Badge>
                                                )}
                                                {product.battery_health && (() => {
                                                    const battery = getBatteryDisplay(product.battery_health);
                                                    return battery ? (
                                                        <Badge className={`border rounded-full text-[10px] px-2 py-0.5 font-bold flex items-center gap-0.5 ${battery.color}`}>
                                                            <Battery className="w-2.5 h-2.5" />
                                                            {battery.label}
                                                        </Badge>
                                                    ) : null;
                                                })()}
                                                {product.vat_regime ? (
                                                    <Badge className={`border rounded-full text-[10px] px-1.5 py-0.5 ${product.vat_regime === 'margin' ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                                        {product.vat_regime === 'margin' ? 'M' : 'S'}
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-red-50 text-red-500 border border-red-200 rounded-full text-[10px] px-1.5 py-0.5">
                                                        ?
                                                    </Badge>
                                                )}
                                                {/* Pièces inconnues */}
                                                {product.pieces_inconnues && product.pieces_inconnues.length > 0 && (
                                                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] px-1.5 py-0.5">
                                                        ⚠ {product.pieces_inconnues.join(', ')}
                                                    </Badge>
                                                )}
                                                {/* Lock Status */}
                                                {product.lock_status && product.lock_status !== 'Débloqué' && (
                                                    <Badge className={`border rounded-full text-[10px] px-1.5 py-0.5 ${
                                                        product.lock_status.toLowerCase().includes('icloud') 
                                                            ? 'bg-red-50 text-red-600 border-red-200' 
                                                            : product.lock_status.toLowerCase().includes('mdm')
                                                            ? 'bg-orange-50 text-orange-600 border-orange-200'
                                                            : 'bg-amber-50 text-amber-600 border-amber-200'
                                                    }`}>
                                                        🔒 {product.lock_status}
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Prices - Col 7-9 */}
                                            <div className="col-span-3 flex items-center gap-3">
                                                {/* Coûts ERP */}
                                                <div className="text-right">
                                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Achat</p>
                                                    <p className="font-mono text-sm text-slate-600 font-semibold">
                                                        {product.erp_prix_achat ? `${product.erp_prix_achat.toFixed(0)}€` : '-'}
                                                    </p>
                                                    {product.erp_cout_total && product.erp_cout_total !== product.erp_prix_achat && (
                                                        <p className="text-[9px] text-slate-400">
                                                            Coût: {product.erp_cout_total.toFixed(0)}€
                                                        </p>
                                                    )}
                                                </div>
                                                
                                                <div className="text-right">
                                                    <p className="text-[10px] text-purple-600 uppercase tracking-wide font-semibold">Argus</p>
                                                    <p className="font-mono text-sm text-slate-600 font-semibold">
                                                        {product.argus_price ? `${product.argus_price.toFixed(0)}€` : <span className="text-amber-500">N/A</span>}
                                                    </p>
                                                </div>
                                                
                                                {/* Déductions */}
                                                {product.pricing_deductions && product.pricing_deductions.length > 0 && (
                                                    <div className="text-right">
                                                        <p className="text-[10px] text-rose-500 uppercase tracking-wide font-semibold">Remises</p>
                                                        <div className="flex flex-col items-end">
                                                            {product.pricing_deductions.map((d, i) => (
                                                                <span key={i} className="text-[10px] text-rose-600 font-mono">
                                                                    -{d.amount?.toFixed(0)}€ <span className="text-rose-400">({d.name})</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                        {/* Source indicator: Model vs Range */}
                                                        {product.applied_range && (
                                                            <div className="mt-1">
                                                                {product.applied_range.includes('Modèle:') ? (
                                                                    <span className="text-[9px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">
                                                                        📱 Tarif Modèle
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-medium">
                                                                        📊 Tarif Gamme
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                <div className="text-right min-w-[80px]">
                                                    <p className="text-[10px] text-emerald-600 uppercase tracking-wide font-semibold">Vente</p>
                                                    <div className="flex items-center justify-end gap-1">
                                                        {(() => {
                                                            const displayPrice = product.price || product.manual_price;
                                                            return displayPrice ? (
                                                                <p className="font-mono text-2xl font-bold text-emerald-700">
                                                                    {displayPrice.toFixed(0)}
                                                                    <span className="text-sm font-normal text-emerald-500">€</span>
                                                                </p>
                                                            ) : (
                                                                <p className="font-mono text-2xl font-bold text-slate-400">-</p>
                                                            );
                                                        })()}
                                                        {product.price_variation && product.price_variation !== 0 && (
                                                            <span 
                                                                className={`text-xs font-mono px-1.5 py-0.5 rounded-full font-bold ${product.price_variation > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
                                                                title={`Variation depuis le dernier calcul : ${product.price_variation > 0 ? '+' : ''}${product.price_variation.toFixed(0)}€`}
                                                            >
                                                                {product.price_variation > 0 ? '↑' : '↓'}{Math.abs(product.price_variation).toFixed(0)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {product.manual_price && (
                                                        <p className="text-[10px] text-purple-600 font-semibold">Manuel</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Margin - Col 10-11 */}
                                            <div className="col-span-2 text-right">
                                                <p className="text-[10px] text-purple-600 uppercase tracking-wide font-semibold">Marge nette</p>
                                                {marginData ? (
                                                    <div>
                                                        <p className={`font-mono text-xl font-bold ${marginData.marge_nette >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                            {marginData.marge_nette >= 0 ? '+' : ''}{marginData.marge_nette.toFixed(0)}€
                                                        </p>
                                                        <p className="text-[10px] text-slate-400">TVA: {marginData.tva?.toFixed(0)}€</p>
                                                    </div>
                                                ) : product.erp_marge_nette != null ? (
                                                    <div>
                                                        <p className={`font-mono text-xl font-bold ${product.erp_marge_nette >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                            {product.erp_marge_nette >= 0 ? '+' : ''}{product.erp_marge_nette.toFixed(0)}€
                                                        </p>
                                                        <p className="text-[10px] text-blue-400">ERP</p>
                                                    </div>
                                                ) : (
                                                    <p className="text-slate-400 text-sm">{loadingMargins ? '...' : '-'}</p>
                                                )}
                                            </div>

                                            {/* Actions - Col 12 */}
                                            <div className="col-span-1 flex justify-end items-center gap-2">
                                                {/* Badge Dropdown */}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className={`h-10 w-10 p-0 rounded-full ${product.badge ? 'hover:bg-orange-100' : 'hover:bg-slate-100'}`}
                                                            style={product.badge ? { backgroundColor: product.badge.bg_color + '20' } : {}}
                                                        >
                                                            <Tag className={`h-5 w-5 ${product.badge ? '' : 'text-slate-400'}`} style={product.badge ? { color: product.badge.bg_color } : {}} />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 border-b">
                                                            Appliquer un badge
                                                        </div>
                                                        {badges.map((badge) => (
                                                            <DropdownMenuItem 
                                                                key={badge.id}
                                                                onClick={() => handleApplyBadge(product.id, badge.id)}
                                                                className="flex items-center gap-2"
                                                            >
                                                                <span 
                                                                    className="px-2 py-0.5 text-xs font-bold rounded-full"
                                                                    style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                                                                >
                                                                    {badge.name}
                                                                </span>
                                                                {product.badge_id === badge.id && (
                                                                    <CheckCircle className="w-4 h-4 text-emerald-500 ml-auto" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                        {badges.length === 0 && (
                                                            <div className="px-3 py-2 text-sm text-slate-400">
                                                                Aucun badge créé
                                                            </div>
                                                        )}
                                                        {product.badge && (
                                                            <>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleApplyBadge(product.id, null)}
                                                                    className="text-rose-600"
                                                                >
                                                                    <X className="w-4 h-4 mr-2" />
                                                                    Retirer le badge
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                                
                                                {/* Publish Toggle */}
                                                <button
                                                    onClick={() => handleTogglePublish(product.id, product.is_published)}
                                                    className={`w-10 h-6 rounded-full transition-colors relative ${product.is_published ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                                    title={product.is_published ? 'Dépublier' : 'Publier'}
                                                >
                                                    <span 
                                                        className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${product.is_published ? 'left-5' : 'left-1'}`}
                                                    />
                                                </button>
                                                
                                                {/* Mark as New / Remove from New Button */}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        const isInNew = product.published_at && new Date(product.published_at) > new Date(Date.now() - 72 * 60 * 60 * 1000);
                                                        if (isInNew) {
                                                            handleRemoveFromNew(product.id);
                                                        } else {
                                                            handleMarkAsNew(product.id);
                                                        }
                                                    }}
                                                    disabled={markingAsNew[product.id]}
                                                    className={`h-10 w-10 p-0 rounded-full ${
                                                        product.published_at && new Date(product.published_at) > new Date(Date.now() - 72 * 60 * 60 * 1000)
                                                            ? 'bg-emerald-100 text-emerald-600 hover:bg-red-100 hover:text-red-600'
                                                            : 'hover:bg-amber-50 text-amber-600'
                                                    }`}
                                                    title={product.published_at && new Date(product.published_at) > new Date(Date.now() - 72 * 60 * 60 * 1000)
                                                        ? 'Retirer des Nouveautés (cliquez)'
                                                        : 'Marquer comme Nouveau (72h)'}
                                                >
                                                    {markingAsNew[product.id] ? (
                                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Sparkles className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenPriceEditor(product)}
                                                    className="h-10 w-10 p-0 rounded-full hover:bg-purple-50 text-purple-600"
                                                >
                                                    <Edit className="h-5 w-5" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Price Edit Dialog */}
                <Dialog open={!!editingProduct} onOpenChange={(open) => !open && setEditingProduct(null)}>
                    <DialogContent className="sm:max-w-md bg-white rounded-[2rem] border-0 shadow-[0_20px_60px_-12px_rgba(124,58,237,0.25)]">
                        <DialogHeader>
                            <DialogTitle 
                                className="text-xl font-bold flex items-center gap-2"
                                style={{ fontFamily: 'var(--font-heading)' }}
                            >
                                <div className="w-8 h-8 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <Edit className="w-4 h-4 text-purple-600" />
                                </div>
                                Modifier le prix
                            </DialogTitle>
                        </DialogHeader>
                        {editingProduct && (
                            <div className="space-y-5 py-4">
                                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                    <p className="font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                                        {editingProduct.brand} {editingProduct.model}
                                    </p>
                                    <p className="text-sm text-slate-500 font-mono">{editingProduct.storage_capacity}GB • {editingProduct.imei}</p>
                                </div>
                                
                                <div>
                                    <Label htmlFor="manual-price" className="text-sm font-semibold text-slate-700">Prix de vente (€)</Label>
                                    <Input
                                        id="manual-price"
                                        type="number"
                                        value={manualPrice}
                                        onChange={(e) => setManualPrice(e.target.value)}
                                        placeholder="Laisser vide pour prix auto"
                                        className="mt-2 font-mono text-xl h-14 rounded-2xl border-slate-200 focus:border-purple-400"
                                    />
                                </div>

                                {manualPrice && (
                                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 space-y-3">
                                        {loadingSimulation ? (
                                            <p className="text-sm text-slate-500">Calcul...</p>
                                        ) : marginSimulation ? (
                                            <>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">Coût achat:</span>
                                                    <span className="font-mono text-rose-600 font-semibold">{marginSimulation.cout_total_achat?.toFixed(2)}€</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">Marge brute:</span>
                                                    <span className="font-mono text-slate-700 font-semibold">{marginSimulation.marge_brute?.toFixed(2)}€</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500">TVA ({marginSimulation.tva_regime}):</span>
                                                    <span className="font-mono text-orange-600 font-semibold">-{marginSimulation.tva?.toFixed(2)}€</span>
                                                </div>
                                                <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between">
                                                    <span className="font-bold text-slate-900">Marge nette:</span>
                                                    <span className={`font-mono text-2xl font-bold ${marginSimulation.marge_nette >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                        {marginSimulation.marge_nette >= 0 ? '+' : ''}{marginSimulation.marge_nette?.toFixed(2)}€
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-sm text-slate-500">Simulation non disponible</p>
                                        )}
                                    </div>
                                )}
                                
                                {/* Bouton pour réappliquer le prix automatique si prix manuel actif */}
                                {editingProduct.manual_price && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                                        <p className="text-sm text-amber-700 mb-3">
                                            Ce produit a un prix manuel de <strong>{editingProduct.manual_price}€</strong>.
                                            Vous pouvez réappliquer le prix calculé par les règles du modèle.
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                                            onClick={() => {
                                                setManualPrice('');
                                            }}
                                        >
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Réappliquer prix règle modèle
                                        </Button>
                                        <p className="text-xs text-amber-500 mt-2 text-center">
                                            Cliquez ensuite sur "Enregistrer" pour valider
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter className="gap-3">
                            <DialogClose asChild>
                                <Button variant="outline" className="rounded-full border-slate-200">Annuler</Button>
                            </DialogClose>
                            <Button 
                                onClick={handleSavePrice} 
                                disabled={savingPrice} 
                                className="rounded-full bg-lime-500 text-slate-900 font-bold hover:scale-105 btn-glow-green"
                            >
                                {savingPrice ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Save View Dialog */}
                <Dialog open={saveViewDialogOpen} onOpenChange={setSaveViewDialogOpen}>
                    <DialogContent className="sm:max-w-md bg-white rounded-[2rem] border-0 shadow-[0_20px_60px_-12px_rgba(124,58,237,0.25)]">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold text-slate-900">
                                Sauvegarder la vue
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div>
                                <Label htmlFor="view-name" className="text-sm font-medium text-slate-700">
                                    Nom de la vue
                                </Label>
                                <Input
                                    id="view-name"
                                    placeholder="Ex: iPhone 11 à publier"
                                    value={newViewName}
                                    onChange={(e) => setNewViewName(e.target.value)}
                                    className="mt-1 rounded-xl"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveView()}
                                />
                            </div>
                            <div className="bg-slate-50 rounded-xl p-3">
                                <p className="text-xs text-slate-500 mb-2">Filtres actuels qui seront sauvegardés :</p>
                                <div className="flex flex-wrap gap-1">
                                    {search && <Badge variant="secondary" className="text-xs">Recherche: {search}</Badge>}
                                    {brandFilter.length > 0 && <Badge variant="secondary" className="text-xs">Marques: {brandFilter.length}</Badge>}
                                    {modelFilter.length > 0 && <Badge variant="secondary" className="text-xs">Modèles: {modelFilter.length}</Badge>}
                                    {statusFilter.length > 0 && <Badge variant="secondary" className="text-xs">États: {statusFilter.length}</Badge>}
                                    {erpStatusFilter.length > 0 && <Badge variant="secondary" className="text-xs">Statuts ERP: {erpStatusFilter.length}</Badge>}
                                    {publishedFilter.length > 0 && <Badge variant="secondary" className="text-xs">Publication: {publishedFilter.length}</Badge>}
                                    {availabilityFilter.length > 0 && <Badge variant="secondary" className="text-xs">Disponibilité: {availabilityFilter.length}</Badge>}
                                    {defectGroupFilter.length > 0 && <Badge variant="secondary" className="text-xs">Pannes: {defectGroupFilter.length}</Badge>}
                                    {lockStatusFilter.length > 0 && <Badge variant="secondary" className="text-xs">Verrouillage: {lockStatusFilter.length}</Badge>}
                                    {!search && brandFilter.length === 0 && modelFilter.length === 0 && statusFilter.length === 0 && (
                                        <span className="text-xs text-slate-400">Aucun filtre actif</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button variant="outline" className="rounded-full">Annuler</Button>
                            </DialogClose>
                            <Button
                                onClick={handleSaveView}
                                disabled={savingView || !newViewName.trim()}
                                className="rounded-full bg-blue-600 hover:bg-blue-700"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {savingView ? 'Sauvegarde...' : 'Sauvegarder'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                
                {/* Badge Manager Dialog */}
                <Dialog open={badgeManagerOpen} onOpenChange={setBadgeManagerOpen}>
                    <DialogContent className="sm:max-w-lg bg-white rounded-[2rem] border-0 shadow-[0_20px_60px_-12px_rgba(249,115,22,0.25)]">
                        <DialogHeader>
                            <DialogTitle 
                                className="text-xl font-bold flex items-center gap-2"
                                style={{ fontFamily: 'var(--font-heading)' }}
                            >
                                <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
                                    <Tag className="w-4 h-4 text-orange-600" />
                                </div>
                                Gérer les Badges
                            </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            {/* Create new badge */}
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                <p className="text-sm font-semibold text-slate-700 mb-3">Créer un nouveau badge</p>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Nom du badge (ex: PROMO)"
                                        value={newBadgeName}
                                        onChange={(e) => setNewBadgeName(e.target.value.toUpperCase())}
                                        className="flex-1 rounded-full"
                                    />
                                    <input
                                        type="color"
                                        value={newBadgeColor}
                                        onChange={(e) => setNewBadgeColor(e.target.value)}
                                        className="w-10 h-10 rounded-full border-2 border-slate-200 cursor-pointer"
                                        title="Couleur du badge"
                                    />
                                    <Button
                                        onClick={handleCreateBadge}
                                        className="rounded-full bg-orange-500 hover:bg-orange-600"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                {/* Preview */}
                                {newBadgeName && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <span className="text-xs text-slate-500">Aperçu:</span>
                                        <span 
                                            className="px-3 py-1 rounded-full text-xs font-bold"
                                            style={{ backgroundColor: newBadgeColor, color: '#ffffff' }}
                                        >
                                            {newBadgeName}
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Existing badges */}
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-slate-700">Badges existants</p>
                                {badges.length === 0 ? (
                                    <p className="text-sm text-slate-400 py-4 text-center">Aucun badge créé</p>
                                ) : (
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {badges.map((badge) => (
                                            <div 
                                                key={badge.id}
                                                className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 hover:border-orange-200 transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span 
                                                        className="px-3 py-1 rounded-full text-xs font-bold"
                                                        style={{ backgroundColor: badge.bg_color, color: badge.text_color }}
                                                    >
                                                        {badge.name}
                                                    </span>
                                                    <input
                                                        type="color"
                                                        value={badge.bg_color}
                                                        onChange={(e) => handleUpdateBadge(badge.id, { bg_color: e.target.value })}
                                                        className="w-6 h-6 rounded-full border-0 cursor-pointer"
                                                        title="Modifier la couleur"
                                                    />
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDeleteBadge(badge.id)}
                                                    className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setBadgeManagerOpen(false)} className="rounded-full">
                                Fermer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                
                {/* Bulk Price Modal */}
                <Dialog open={bulkPriceModalOpen} onOpenChange={setBulkPriceModalOpen}>
                    <DialogContent className="sm:max-w-md bg-white rounded-[2rem] border-0 shadow-[0_20px_60px_-12px_rgba(16,185,129,0.25)]">
                        <DialogHeader>
                            <DialogTitle 
                                className="text-xl font-bold flex items-center gap-2"
                                style={{ fontFamily: 'var(--font-heading)' }}
                            >
                                <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center">
                                    <Euro className="w-4 h-4 text-emerald-600" />
                                </div>
                                Prix de vente en masse
                            </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
                                <p className="text-sm text-emerald-800 mb-1">
                                    <strong>{selectedProducts.length}</strong> produit(s) sélectionné(s)
                                </p>
                                <p className="text-xs text-emerald-600">
                                    Le prix manuel sera appliqué à tous ces produits
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700">
                                    Prix de vente (€ HT)
                                </label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="Ex: 40"
                                        value={bulkPrice}
                                        onChange={(e) => setBulkPrice(e.target.value)}
                                        className="flex-1 text-lg font-mono text-emerald-700 font-bold"
                                    />
                                    <span className="flex items-center text-lg font-bold text-slate-500">€</span>
                                </div>
                            </div>
                            
                            {bulkPrice && parseFloat(bulkPrice) > 0 && (
                                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                                    <p className="text-sm text-slate-600">
                                        Tous les produits seront vendus à <strong className="text-emerald-700">{parseFloat(bulkPrice).toFixed(2)} €</strong> HT
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        <DialogFooter className="flex gap-2">
                            <Button 
                                variant="outline" 
                                onClick={handleBulkClearManualPrice}
                                disabled={applyingBulkPrice}
                                className="rounded-full text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                                Retirer prix manuel
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => { setBulkPriceModalOpen(false); setBulkPrice(''); }} 
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleBulkManualPrice}
                                disabled={applyingBulkPrice || !bulkPrice || parseFloat(bulkPrice) <= 0}
                                className="rounded-full bg-emerald-500 hover:bg-emerald-600"
                            >
                                {applyingBulkPrice ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        Application...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Appliquer
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminProducts;
